import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type { Segment as ApiSegment } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Segment entity — workspace-scoped audiences.
 *
 * Minimal detail-only entity: id normalized to `string` (layer-wide
 * convention even though the wire ships a number) and the epoch timestamps
 * transformed to `Date`. List/mutation surface can be added when a consumer
 * needs it; today only the by-id detail read exists.
 */
export type Segment = Omit<ApiSegment, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

const SegmentFromApi = readSchema<Segment>(
  z.looseObject({
    id: z.number().transform(String),
    created_at: epochSecondsToDate,
    updated_at: epochSecondsToDate,
  }),
);

/** @public */
export const segment = defineEntity<Segment>({
  name: 'segment',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SegmentFromApi },
});

export const segmentKeys = {
  ...segment.keys,
  /** Live member count (`segments/{id}/count`). */
  count: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'count', id] as const,
  /** Segment metrics (`segments/{id}/metrics`). */
  metrics: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'metrics', id] as const,
  /** Build status (`segments/{id}/status`). */
  status: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'status', id] as const,
  /** Per-segment usage (`segments/{id}/usage`). */
  usage: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'usage', id] as const,
  /** Bulk usage map (`segments_usage?segment_ids=…`), keyed by the sorted id set. */
  usageBulk: (workspaceId: number, ids: readonly string[]) =>
    [...segment.keys.all(workspaceId), 'usage', 'bulk', [...ids].sort()] as const,
  /** Server-side manual-segment search (the add-to-segment picker), keyed by term. */
  manualOptions: (workspaceId: number, search: string) =>
    [...segment.keys.all(workspaceId), 'manual-options', search] as const,
};

export function parseSegment(raw: ApiSegment): Segment {
  return segment.parse(raw);
}
